import type {
  Category,
  CategoryBreakdown,
  Difficulty,
  DifficultyBreakdown,
  DifficultySlug,
  LeaderboardResponse,
  LeaderboardScope,
  MeResponse,
  UserStats,
} from '../../shared/types.ts';

async function http<T>(url: string, init: RequestInit = {}): Promise<T> {
  const res = await fetch(url, {
    credentials: 'include',
    headers: { 'Content-Type': 'application/json' },
    ...init,
  });
  if (!res.ok) {
    const body = (await res.json().catch(() => ({}))) as { error?: string };
    throw new Error(body.error ?? `HTTP ${res.status}`);
  }
  return (await res.json()) as T;
}

export const api = {
  me: () => http<MeResponse>('/api/me'),
  register: (username: string, password: string, displayName?: string) =>
    http<{ ok: true; userId: number }>('/api/auth/register', {
      method: 'POST',
      body: JSON.stringify({ username, password, displayName }),
    }),
  login: (username: string, password: string) =>
    http<{ ok: true; userId: number }>('/api/auth/login', {
      method: 'POST',
      body: JSON.stringify({ username, password }),
    }),
  logout: () => http<{ ok: true }>('/api/auth/logout', { method: 'POST' }),
  setSettings: (soundEnabled: boolean, reduceMotion: boolean) =>
    http<{ ok: true }>('/api/me/settings', {
      method: 'POST',
      body: JSON.stringify({ soundEnabled, reduceMotion }),
    }),
  setProfile: (displayName: string, avatarColor: string) =>
    http<{ ok: true }>('/api/me/profile', {
      method: 'POST',
      body: JSON.stringify({ displayName, avatarColor }),
    }),
  myStats: () =>
    http<{ overall: UserStats; byCategory: CategoryBreakdown[]; byDifficulty: DifficultyBreakdown[] }>(
      '/api/stats/me',
    ),
  userStats: (username: string) =>
    http<{
      user: { id: number; username: string; displayName: string; avatarColor: string; createdAt: string };
      overall: UserStats;
      byCategory: CategoryBreakdown[];
      byDifficulty: DifficultyBreakdown[];
    }>(`/api/stats/user/${encodeURIComponent(username)}`),
  globalStats: () =>
    http<{
      totalRounds: number;
      totalAnswers: number;
      totalCorrect: number;
      totalSkipped: number;
      players: number;
      globalAccuracyPct: number;
    }>('/api/stats/global'),
  categories: () => http<Category[]>('/api/stats/categories'),
  difficulties: () => http<Difficulty[]>('/api/stats/difficulties'),
  leaderboard: (params: {
    scope: LeaderboardScope;
    category?: string;
    difficulty?: DifficultySlug;
    limit?: number;
    onlyUsers?: boolean;
  }) => {
    const u = new URLSearchParams();
    u.set('scope', params.scope);
    if (params.category) u.set('category', params.category);
    if (params.difficulty) u.set('difficulty', params.difficulty);
    if (params.limit) u.set('limit', String(params.limit));
    if (params.onlyUsers) u.set('onlyUsers', 'true');
    return http<LeaderboardResponse>(`/api/leaderboard?${u.toString()}`);
  },
};
